
import React, { useState, useEffect } from "react";
import { Home, Search, Bell, PlusCircle, User, Image, BookOpen, PenTool, Users, Heart, BriefcaseIcon, ShoppingBag, Store } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import { ExpandableTabs } from "./ui/expandable-tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NotificationBadge } from "./ui/notification-badge";
import { useNotifications } from "@/hooks/use-notifications";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "./ui/button";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useUser } from "@/hooks/use-user";

export const MobileTabBar = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showManagementOptions, setShowManagementOptions] = useState(false);
  const [showServicesSheet, setShowServicesSheet] = useState(false);
  const { user, isLoading: userIsLoading } = useUser();
  const { unreadCount } = useNotifications(user?.id);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsVisible(currentScrollY <= lastScrollY || currentScrollY < 100);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const handleTabChange = (index: number | null) => {
    if (index === null) return;
    
    if (index === 2) { // Plus icon (Create) tab
      if (!user) {
        toast.error("Você precisa estar logado para criar conteúdo");
        navigate('/auth');
        return;
      }
      setShowServicesSheet(true);
    } else if (index === 4) { // Profile tab (User icon)
      if (!user) {
        navigate('/auth');
        return;
      }
      setShowManagementOptions(true);
    } else {
      const tabs = [
        { path: '/' },
        { path: '/search' },
        { path: '/create' }, // This will be handled by sheet now
        { path: '/notifications' },
      ];
      
      const selectedTab = tabs[index];
      if (selectedTab && selectedTab.path) {
        // Check if we're navigating to notifications and user isn't logged in
        if (index === 3 && !user) {
          toast.error("Você precisa estar logado para ver notificações");
          navigate('/auth');
          return;
        }
        
        navigate(selectedTab.path);
      }
    }
  };

  const handleNavigateToManagement = (path: string) => {
    if (!user) {
      toast.error("Você precisa estar logado para gerenciar conteúdo");
      navigate('/auth');
      return;
    }
    navigate(path);
    setShowManagementOptions(false);
  };

  const handleNavigateToServices = (path: string) => {
    if (!user) {
      toast.error("Você precisa estar logado para acessar serviços");
      navigate('/auth');
      return;
    }
    navigate(path);
    setShowServicesSheet(false);
  };

  // Define the tabs with proper typing for the ExpandableTabs component
  const mobileTabs = [
    { 
      title: "Home", 
      icon: <Home className="h-6 w-6" /> 
    },
    { 
      title: "Explore", 
      icon: <Search className="h-6 w-6" /> 
    },
    { 
      title: "Create", 
      icon: <PlusCircle className="h-6 w-6" /> 
    },
    { 
      title: "Notif.", 
      icon: <div className="relative">
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && <NotificationBadge count={unreadCount} />}
      </div> 
    },
    { 
      title: "Profile", 
      icon: user && user.avatar_url ? 
      <Avatar className="h-7 w-7">
        <AvatarImage src={user.avatar_url} alt={user.username || user.email || "User"} />
        <AvatarFallback>{user.username?.charAt(0).toUpperCase() || (user.email && user.email.charAt(0).toUpperCase()) || "U"}</AvatarFallback>
      </Avatar> : 
      <User className="h-6 w-6" /> 
    }
  ];

  return (
    <>
      <div className={cn(
        "fixed bottom-4 left-0 right-0 z-50 transition-all duration-300 flex justify-center sm:hidden",
        isVisible ? "translate-y-0" : "translate-y-full"
      )}>
        <ExpandableTabs 
          tabs={mobileTabs} 
          className="mx-auto bg-background/80 backdrop-blur-lg border-border shadow-lg"
          onChange={handleTabChange}
        />
      </div>
      
      {/* Content Management Sheet */}
      <Sheet open={showManagementOptions} onOpenChange={setShowManagementOptions}>
        <SheetContent side="bottom" className="h-auto rounded-t-xl">
          <SheetHeader className="mb-4">
            <SheetTitle>Gerenciar conteúdo</SheetTitle>
            <SheetDescription>
              Acesse seus itens e estatísticas
            </SheetDescription>
          </SheetHeader>
          
          <div className="grid grid-cols-3 gap-4">
            <Button 
              variant="outline" 
              className="flex flex-col items-center justify-center h-24 p-2" 
              onClick={() => handleNavigateToManagement('/my-pins')}
            >
              <Image className="h-8 w-8 mb-2" />
              Meus Pins
            </Button>
            
            <Button 
              variant="outline" 
              className="flex flex-col items-center justify-center h-24 p-2" 
              onClick={() => handleNavigateToManagement('/my-webtoons')}
            >
              <PenTool className="h-8 w-8 mb-2" />
              Meus Webtoons
            </Button>
            
            <Button 
              variant="outline" 
              className="flex flex-col items-center justify-center h-24 p-2" 
              onClick={() => handleNavigateToManagement('/my-webnovels')}
            >
              <BookOpen className="h-8 w-8 mb-2" />
              Meus Webnovels
            </Button>
          </div>
          
          <SheetFooter className="mt-4">
            <Button 
              variant="default" 
              onClick={() => navigate(user ? `/profile/${user.id}` : '/auth')}
              className="w-full"
            >
              Ir para perfil
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      
      {/* Services Sheet with Animation */}
      <Sheet open={showServicesSheet} onOpenChange={setShowServicesSheet}>
        <SheetContent side="bottom" className="h-auto rounded-t-xl px-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <SheetHeader className="px-6 mb-4">
              <SheetTitle>Gerenciar Serviços</SheetTitle>
              <SheetDescription>
                Acesse suas ferramentas de trabalho e comércio
              </SheetDescription>
            </SheetHeader>
            
            <div className="space-y-3 px-6">
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Button 
                  variant="outline" 
                  className="flex items-center justify-start w-full px-4 py-3 h-auto" 
                  onClick={() => handleNavigateToServices('/collabs')}
                >
                  <Users className="h-5 w-5 mr-4" />
                  <div className="text-left">
                    <div className="font-medium">Colabs</div>
                    <div className="text-xs text-muted-foreground">Encontre colaboradores para seus projetos</div>
                  </div>
                </Button>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Button 
                  variant="outline" 
                  className="flex items-center justify-start w-full px-4 py-3 h-auto" 
                  onClick={() => handleNavigateToServices('/commissions')}
                >
                  <Heart className="h-5 w-5 mr-4" />
                  <div className="text-left">
                    <div className="font-medium">Commissions</div>
                    <div className="text-xs text-muted-foreground">Aceite trabalhos personalizados</div>
                  </div>
                </Button>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Button 
                  variant="outline" 
                  className="flex items-center justify-start w-full px-4 py-3 h-auto" 
                  onClick={() => handleNavigateToServices('/jobs')}
                >
                  <BriefcaseIcon className="h-5 w-5 mr-4" />
                  <div className="text-left">
                    <div className="font-medium">Jobs</div>
                    <div className="text-xs text-muted-foreground">Encontre oportunidades de trabalho</div>
                  </div>
                </Button>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Button 
                  variant="outline" 
                  className="flex items-center justify-start w-full px-4 py-3 h-auto" 
                  onClick={() => handleNavigateToServices('/shop')}
                >
                  <ShoppingBag className="h-5 w-5 mr-4" />
                  <div className="text-left">
                    <div className="font-medium">Ink Shop</div>
                    <div className="text-xs text-muted-foreground">Compre produtos oficiais</div>
                  </div>
                </Button>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Button 
                  variant="outline" 
                  className="flex items-center justify-start w-full px-4 py-3 h-auto" 
                  onClick={() => handleNavigateToServices('/my-store')}
                >
                  <Store className="h-5 w-5 mr-4" />
                  <div className="text-left">
                    <div className="font-medium">Minha Loja</div>
                    <div className="text-xs text-muted-foreground">Gerencie sua loja personalizada</div>
                  </div>
                </Button>
              </motion.div>
            </div>
            
            <motion.div 
              className="mt-6 px-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Button 
                variant="default" 
                onClick={() => setShowServicesSheet(false)}
                className="w-full"
              >
                Fechar
              </Button>
            </motion.div>
          </motion.div>
        </SheetContent>
      </Sheet>
    </>
  );
};
